import {
  useEffect,
  useState,
  useCallback,
  useRef,
  forwardRef,
  useImperativeHandle,
  ChangeEvent,
} from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Chip,
  Checkbox,
  Divider,
  CircularProgress,
} from '@mui/material';
import { Plus, X, PencilSimple } from '@phosphor-icons/react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createBrandApi,
  getAllBrandsApiV2,
  getRestaurantApi,
  updateBrandsApiV2,
  updateRestaurant,
  getGoogleLocationsGeocode,
} from '../../../../utils/apis';
import { useUser } from '../../../../hooks/useUser';
import {
  ICreateBrand,
  IBusinessCreate,
} from '../../../../schemas/apis/interface';
import { useAlert } from '../../../../hooks/useAlert';
import MessagePopup from '../../../common/MessagePopup';
import LocationAutocomplete from '../../../common/LocationAutoComplete';
import { debounce } from 'lodash';

interface BusinessAndBrandStepProps {
  onValidationChange: (isValid: boolean) => void;
}

export interface BusinessAndBrandStepRef {
  saveBusinessData: () => Promise<void>;
}

const BusinessAndBrandStep = forwardRef<
  BusinessAndBrandStepRef,
  BusinessAndBrandStepProps
>(({ onValidationChange }, ref) => {
  const { user } = useUser();
  const queryClient = useQueryClient();
  const { alert, setAlert, showAlert } = useAlert();
  const [userInput, setUserInput] = useState<string>('');
  const selectionMadeRef = useRef<boolean>(false);

  const [businessData, setBusinessData] = useState<IBusinessCreate>({
    name: '',
    legal_name: '',
    contacts: [
      {
        contact_type: 'work',
        poc_name: '',
        poc_email: '',
        poc_phone: '',
        designation: '',
      },
    ],
    hq_location: {
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      country: '',
      zip_code: '',
      latitude: 0,
      longitude: 0,
    },
    is_active: true,
  });

  const [brandFormData, setBrandFormData] = useState<ICreateBrand>({
    name: '',
    poc_at_brand: '',
    poc_at_email: '',
    poc_role: '',
    poc_contact: '',
  });

  const [sameAsBusinessContact, setSameAsBusinessContact] = useState(false);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [editingBrandId, setEditingBrandId] = useState<string | null>(null);
  const [showBrandForm, setShowBrandForm] = useState(false);

  // Fetch existing restaurant/business data
  const { data: restaurantData, isLoading: isLoadingRestaurant } = useQuery({
    queryKey: ['restaurants', user?.tenantName],
    queryFn: () => getRestaurantApi(user?.tenantName || ''),
    enabled: !!user?.tenantName,
    staleTime: Infinity,
    gcTime: 1000 * 60 * 30,
    refetchOnMount: false,
  });

  // Fetch existing brands
  const { data: brandsData, isLoading: isLoadingBrands } = useQuery({
    queryKey: ['brands', user?.tenantName],
    queryFn: () => getAllBrandsApiV2(user?.tenantName || ''),
    enabled: !!user?.tenantName,
    staleTime: Infinity,
    gcTime: 1000 * 60 * 30,
    refetchOnMount: false,
  });

  // Initialize business data from API
  useEffect(() => {
    // Access the first restaurant from the nested data array
    if (restaurantData?.data?.data?.[0]) {
      const data = restaurantData.data.data[0];
      setBusinessData({
        name: data.name || '',
        legal_name: data.legal_name || data.name || '',
        contacts: data.contacts || [
          {
            contact_type: 'work',
            poc_name: '',
            poc_email: '',
            poc_phone: '',
            designation: '',
          },
        ],
        hq_location: data.hq_location || {
          address_line1: '',
          address_line2: '',
          city: '',
          state: '',
          country: '',
          zip_code: '',
          latitude: 0,
          longitude: 0,
        },
        is_active: data.is_active ?? true,
        business_id: data.business_id,
      });

      if (data.hq_location?.address_line1) {
        setUserInput(data.hq_location.address_line1);
      }
    }
  }, [restaurantData]);

  // Initialize showBrandForm based on existing brands
  useEffect(() => {
    // Show form initially if no brands exist
    if (brandsData?.data?.data !== undefined) {
      if (brandsData.data.data.length === 0) {
        setShowBrandForm(true);
      }
    }
  }, [brandsData]);

  // Debounced geocode function
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedGeocode = useCallback(
    debounce(async (location: string) => {
      if (!location.trim() || !selectionMadeRef.current) return;

      try {
        const geocodeResponse = await getGoogleLocationsGeocode({
          query: location,
        });

        const geocodeData = geocodeResponse.data.results[0];
        const addressComponents = geocodeData.address_components;

        const getComponent = (type: string, short = false) => {
          const component = addressComponents.find((comp: any) =>
            comp.types.includes(type)
          );
          return component
            ? short
              ? component.short_name
              : component.long_name
            : '';
        };

        setBusinessData(prev => ({
          ...prev,
          hq_location: {
            ...prev.hq_location,
            address_line1: geocodeData.formatted_address,
            city: getComponent('locality'),
            state: getComponent('administrative_area_level_1', true),
            country: getComponent('country'),
            zip_code: getComponent('postal_code'),
            latitude: geocodeData.geometry.location.lat,
            longitude: geocodeData.geometry.location.lng,
          },
        }));
      } catch (error) {
        console.error('Geocoding error:', error);
      }
    }, 500),
    []
  );

  const handleLocationChange = (value: string, isSelection = false) => {
    setUserInput(value);
    selectionMadeRef.current = isSelection;

    if (isSelection) {
      debouncedGeocode(value);
    }
  };

  // Create brand mutation
  const { mutateAsync: createBrand, isPending: isCreating } = useMutation({
    mutationFn: (formData: ICreateBrand) =>
      createBrandApi(formData, user?.tenantName || ''),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brands', user?.tenantName] });
      showAlert('Brand created successfully', 'success');
      resetBrandForm();
      setEditingBrandId(null);
      setShowBrandForm(false);
    },
    onError: (error: any) => {
      showAlert(
        error?.response?.data?.message || 'Failed to create brand',
        'error'
      );
    },
  });

  // Update brand mutation
  const { mutateAsync: updateBrand, isPending: isUpdating } = useMutation({
    mutationFn: ({
      formData,
      brandId,
    }: {
      formData: ICreateBrand;
      brandId: string;
    }) => updateBrandsApiV2(formData, user?.tenantName || '', brandId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brands', user?.tenantName] });
      showAlert('Brand updated successfully', 'success');
      resetBrandForm();
      setEditingBrandId(null);
      setShowBrandForm(false);
    },
    onError: (error: any) => {
      showAlert(
        error?.response?.data?.message || 'Failed to update brand',
        'error'
      );
    },
  });

  // Expose saveBusinessData function to parent via ref
  useImperativeHandle(ref, () => ({
    saveBusinessData: async () => {
      // Validate at least one brand exists
      if (!brandsData?.data?.data?.length) {
        showAlert('Please add at least one brand', 'error');
        throw new Error('No brands added');
      }

      const sanitizedData: IBusinessCreate = {
        ...businessData,
        name: businessData.name || '',
        legal_name: businessData.name || '',
        contacts: [
          {
            contact_type: 'work',
            poc_name: businessData.contacts?.[0]?.poc_name || '',
            poc_email: businessData.contacts?.[0]?.poc_email || '',
            poc_phone: businessData.contacts?.[0]?.poc_phone || '',
            designation: businessData.contacts?.[0]?.designation || '',
          },
        ],
        hq_location: {
          ...businessData.hq_location,
          address_line1: businessData.hq_location?.address_line1 || '',
          address_line2: businessData.hq_location?.address_line2 || '',
          city: businessData.hq_location?.city || '',
          state: businessData.hq_location?.state || '',
          country: businessData.hq_location?.country || '',
          zip_code: businessData.hq_location?.zip_code || '',
        },
        is_active: businessData.is_active ?? true,
      };

      if (businessData.business_id && user?.tenantName) {
        await updateRestaurant(
          sanitizedData,
          user.tenantName,
          businessData.business_id.toString()
        );
        queryClient.invalidateQueries({ queryKey: ['restaurants'] });
      }
    },
  }));

  // Validation
  useEffect(() => {
    const isValid = !!(
      businessData.name &&
      businessData.hq_location?.address_line1 &&
      businessData.contacts?.[0]?.poc_name &&
      businessData.contacts?.[0]?.poc_email &&
      businessData.contacts?.[0]?.poc_phone &&
      brandsData?.data?.data?.length > 0
    );
    onValidationChange(isValid);
  }, [businessData, brandsData, onValidationChange]);

  const validateEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const resetBrandForm = () => {
    setBrandFormData({
      name: '',
      poc_at_brand: '',
      poc_at_email: '',
      poc_role: '',
      poc_contact: '',
    });
    setSameAsBusinessContact(false);
    setIsFormSubmitted(false);
  };

  const handleBrandInputChange = (field: keyof ICreateBrand, value: string) => {
    setBrandFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setSameAsBusinessContact(checked);

    if (checked && businessData.contacts?.[0]) {
      const businessContact = businessData.contacts[0];
      setBrandFormData(prev => ({
        ...prev,
        poc_at_brand: businessContact.poc_name || '',
        poc_at_email: businessContact.poc_email || '',
        poc_role: businessContact.designation || '',
        poc_contact: businessContact.poc_phone || '',
      }));
    } else if (!checked) {
      setBrandFormData(prev => ({
        ...prev,
        poc_at_brand: '',
        poc_at_email: '',
        poc_role: '',
        poc_contact: '',
      }));
    }
  };

  const handleBrandSave = async () => {
    setIsFormSubmitted(true);

    // Validate
    if (!brandFormData.name) {
      showAlert('Brand name is required', 'error');
      return;
    }
    if (
      brandFormData.poc_at_email &&
      !validateEmail(brandFormData.poc_at_email)
    ) {
      showAlert('Invalid email address', 'error');
      return;
    }
    if (
      brandFormData.poc_contact &&
      brandFormData.poc_contact.length > 0 &&
      brandFormData.poc_contact.length < 10
    ) {
      showAlert('Contact number must be at least 10 digits', 'error');
      return;
    }

    const sanitizedData = {
      ...brandFormData,
      poc_at_brand: brandFormData.poc_at_brand || '',
      poc_at_email: brandFormData.poc_at_email || '',
      poc_role: brandFormData.poc_role || '',
      poc_contact: brandFormData.poc_contact || '',
    };

    try {
      if (editingBrandId) {
        await updateBrand({ formData: sanitizedData, brandId: editingBrandId });
      } else {
        await createBrand(sanitizedData);
      }
    } catch (error) {
      console.error('Error saving brand:', error);
    }
  };

  const handleEditBrand = (brand: ICreateBrand) => {
    setBrandFormData({
      name: brand.name,
      poc_at_brand: brand.poc_at_brand || '',
      poc_at_email: brand.poc_at_email || '',
      poc_role: brand.poc_role || '',
      poc_contact: brand.poc_contact || '',
    });
    setEditingBrandId(brand.id || null);
    setShowBrandForm(true);
    setIsFormSubmitted(false);
  };

  const handleCancelEdit = () => {
    setEditingBrandId(null);
    resetBrandForm();
    setShowBrandForm(false);
  };

  const isPending = isCreating || isUpdating;
  const brands = brandsData?.data?.data || [];

  // Debug logging
  console.log('Brand UI Debug:', {
    brandsDataRaw: brandsData,
    brandsArray: brands,
    brandsLength: brands.length,
    showBrandForm,
    shouldShowForm: brands.length === 0 || showBrandForm,
    shouldShowList: brands.length > 0 && !showBrandForm,
  });

  if (isLoadingRestaurant || isLoadingBrands) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        maxWidth: '1200px',
        width: '100%',
        margin: '0 auto',
        py: 4,
        px: 2,
      }}
    >
      <MessagePopup alert={alert} setAlert={setAlert} />

      {/* Heading */}
      <Box
        sx={{
          maxWidth: '900px',
          mx: 'auto',
          mb: 3,
        }}
      >
        <Typography
          sx={{
            fontSize: '24px',
            fontWeight: 600,
            color: 'custom.textBlue',
            mb: 1,
          }}
        >
          Business & Brand Setup
        </Typography>
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: 400,
            color: 'lightGray.text',
          }}
        >
          Set up your business details and create at least one brand
        </Typography>
      </Box>

      {/* Business Details Section */}
      <Box
        sx={{
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'lightGray.main',
          borderRadius: '8px',
          p: 4,
          mb: 3,
          maxWidth: '900px',
          mx: 'auto',
        }}
      >
        <Typography
          sx={{
            fontSize: '18px',
            fontWeight: 600,
            color: 'custom.textBlue',
            mb: 2,
          }}
        >
          Business Details
        </Typography>

        {/* Business Name */}
        <Box sx={{ mb: 2 }}>
          <Typography
            sx={{
              fontSize: '12px',
              fontWeight: 400,
              color: 'custom.textBlue',
              mb: 0.5,
            }}
          >
            Business Name *
          </Typography>
          <TextField
            fullWidth
            placeholder="Enter Business Name"
            value={businessData.name}
            onChange={e =>
              setBusinessData(prev => ({
                ...prev,
                name: e.target.value,
              }))
            }
            sx={{
              '& .MuiOutlinedInput-root': {
                height: '40px',
                borderRadius: '6px',
                '& fieldset': {
                  borderWidth: '1px',
                  borderColor: 'lightGray.main',
                },
                '&:hover fieldset': {
                  borderColor: '#D0D5DD',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                  borderWidth: '1px',
                },
              },
              '& .MuiOutlinedInput-input': {
                padding: '8px 12px',
                fontSize: '14px',
              },
            }}
          />
        </Box>

        {/* Legal Name */}
        <Box sx={{ mb: 2 }}>
          <Typography
            sx={{
              fontSize: '12px',
              fontWeight: 400,
              color: 'custom.textBlue',
              mb: 0.5,
            }}
          >
            Legal Name
          </Typography>
          <TextField
            fullWidth
            placeholder="Enter Legal Name (optional)"
            value={businessData.legal_name || businessData.name}
            onChange={e =>
              setBusinessData(prev => ({
                ...prev,
                legal_name: e.target.value,
              }))
            }
            sx={{
              '& .MuiOutlinedInput-root': {
                height: '40px',
                borderRadius: '6px',
                '& fieldset': {
                  borderWidth: '1px',
                  borderColor: 'lightGray.main',
                },
                '&:hover fieldset': {
                  borderColor: '#D0D5DD',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                  borderWidth: '1px',
                },
              },
              '& .MuiOutlinedInput-input': {
                padding: '8px 12px',
                fontSize: '14px',
              },
            }}
          />
        </Box>

        {/* Business Location */}
        <Box sx={{ mb: 2 }}>
          <Typography
            sx={{
              fontSize: '12px',
              fontWeight: 400,
              color: 'custom.textBlue',
              mb: 0.5,
            }}
          >
            Business Location *
          </Typography>
          <LocationAutocomplete
            value={userInput}
            onChange={handleLocationChange}
            placeholder="Enter your business address"
            width="100%"
          />
        </Box>

        {/* Contact Details */}
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: 500,
            color: 'custom.textBlue',
            mb: 1,
            mt: 2,
          }}
        >
          Contact Details *
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Contact Name */}
          <Box>
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 400,
                color: 'custom.textBlue',
                mb: 0.5,
              }}
            >
              Name *
            </Typography>
            <TextField
              fullWidth
              placeholder="Enter contact name"
              value={businessData.contacts?.[0]?.poc_name || ''}
              onChange={e =>
                setBusinessData(prev => ({
                  ...prev,
                  contacts: [
                    {
                      ...prev.contacts![0],
                      poc_name: e.target.value,
                    },
                  ],
                }))
              }
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: '40px',
                  borderRadius: '6px',
                  '& fieldset': {
                    borderWidth: '1px',
                    borderColor: 'lightGray.main',
                  },
                  '&:hover fieldset': {
                    borderColor: '#D0D5DD',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: '1px',
                  },
                },
                '& .MuiOutlinedInput-input': {
                  padding: '8px 12px',
                  fontSize: '14px',
                },
              }}
            />
          </Box>

          {/* Contact Email */}
          <Box>
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 400,
                color: 'custom.textBlue',
                mb: 0.5,
              }}
            >
              Email *
            </Typography>
            <TextField
              fullWidth
              type="email"
              placeholder="email@example.com"
              value={businessData.contacts?.[0]?.poc_email || ''}
              onChange={e =>
                setBusinessData(prev => ({
                  ...prev,
                  contacts: [
                    {
                      ...prev.contacts![0],
                      poc_email: e.target.value,
                    },
                  ],
                }))
              }
              error={
                businessData.contacts?.[0]?.poc_email
                  ? !validateEmail(businessData.contacts[0].poc_email)
                  : false
              }
              helperText={
                businessData.contacts?.[0]?.poc_email &&
                !validateEmail(businessData.contacts[0].poc_email)
                  ? 'Invalid email address'
                  : ''
              }
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: '40px',
                  borderRadius: '6px',
                  '& fieldset': {
                    borderWidth: '1px',
                    borderColor: 'lightGray.main',
                  },
                  '&:hover fieldset': {
                    borderColor: '#D0D5DD',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: '1px',
                  },
                },
                '& .MuiOutlinedInput-input': {
                  padding: '8px 12px',
                  fontSize: '14px',
                },
              }}
            />
          </Box>

          {/* Contact Phone */}
          <Box>
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 400,
                color: 'custom.textBlue',
                mb: 0.5,
              }}
            >
              Phone *
            </Typography>
            <Box
              sx={{
                '& .special-label': {
                  display: 'none !important',
                },
                '& .form-control::placeholder': {
                  opacity: 0,
                },
              }}
            >
              <PhoneInput
                country={'us'}
                value={businessData.contacts?.[0]?.poc_phone || ''}
                onChange={phone =>
                  setBusinessData(prev => ({
                    ...prev,
                    contacts: [
                      {
                        ...prev.contacts![0],
                        poc_phone: phone,
                      },
                    ],
                  }))
                }
                enableSearch={false}
                disableSearchIcon={true}
                disableDropdown={false}
                searchPlaceholder=""
                placeholder=""
                inputStyle={{
                  width: '100%',
                  height: '40px',
                  fontSize: '14px',
                  borderRadius: '6px',
                  borderColor: '#E2E8F0',
                }}
                buttonStyle={{
                  borderRadius: '6px 0 0 6px',
                  border: '1px solid #E2E8F0',
                }}
                dropdownStyle={{
                  display: 'none',
                }}
                inputProps={{
                  name: 'phone',
                  required: true,
                  placeholder: '',
                }}
              />
            </Box>
          </Box>

          {/* Contact Role */}
          <Box>
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 400,
                color: 'custom.textBlue',
                mb: 0.5,
              }}
            >
              Role / Designation
            </Typography>
            <TextField
              fullWidth
              placeholder="e.g., Manager, Owner"
              value={businessData.contacts?.[0]?.designation || ''}
              onChange={e =>
                setBusinessData(prev => ({
                  ...prev,
                  contacts: [
                    {
                      ...prev.contacts![0],
                      designation: e.target.value,
                    },
                  ],
                }))
              }
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: '40px',
                  borderRadius: '6px',
                  '& fieldset': {
                    borderWidth: '1px',
                    borderColor: 'lightGray.main',
                  },
                  '&:hover fieldset': {
                    borderColor: '#D0D5DD',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: '1px',
                  },
                },
                '& .MuiOutlinedInput-input': {
                  padding: '8px 12px',
                  fontSize: '14px',
                },
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Brand Details Section */}
      <Box
        sx={{
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'lightGray.main',
          borderRadius: '8px',
          p: 4,
          maxWidth: '900px',
          mx: 'auto',
        }}
      >
        {/* Header with title and Add New Brand button */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography
            sx={{
              fontSize: '18px',
              fontWeight: 600,
              color: 'custom.textBlue',
            }}
          >
            Brand Details
          </Typography>
          {brands.length > 0 && !showBrandForm && (
            <Button
              variant="outlined"
              startIcon={<Plus size={16} />}
              onClick={() => {
                resetBrandForm();
                setShowBrandForm(true);
              }}
              sx={{
                textTransform: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 500,
              }}
            >
              Add New Brand
            </Button>
          )}
        </Box>

        {/* Brand Form - Show when no brands exist OR showBrandForm is true */}
        {(brands.length === 0 || showBrandForm) && (
          <Box sx={{ mb: 3 }}>
            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: 500,
                color: 'custom.textBlue',
                mb: 2,
              }}
            >
              {editingBrandId ? 'Edit Brand' : 'Add New Brand'}
            </Typography>

            {/* Brand Name */}
            <Box sx={{ mb: 2 }}>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 400,
                  color: 'custom.textBlue',
                  mb: 0.5,
                }}
              >
                Brand Name *
              </Typography>
              <TextField
                fullWidth
                placeholder="Enter brand name"
                value={brandFormData.name}
                onChange={e => handleBrandInputChange('name', e.target.value)}
                error={isFormSubmitted && !brandFormData.name}
                helperText={
                  isFormSubmitted && !brandFormData.name
                    ? 'Brand name is required'
                    : ''
                }
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: '40px',
                    borderRadius: '6px',
                    '& fieldset': {
                      borderWidth: '1px',
                      borderColor: 'lightGray.main',
                    },
                    '&:hover fieldset': {
                      borderColor: '#D0D5DD',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                      borderWidth: '1px',
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    padding: '8px 12px',
                    fontSize: '14px',
                  },
                }}
              />
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Same as Business Contact Checkbox */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 2,
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: '13px',
                    color: 'text.secondary',
                  }}
                >
                  Same as Business Contact
                </Typography>
              </Box>
              <Checkbox
                checked={sameAsBusinessContact}
                onChange={handleCheckboxChange}
                sx={{
                  color: 'primary.light',
                  '&.Mui-checked': {
                    color: 'primary.main',
                  },
                }}
              />
            </Box>

            {/* POC Name */}
            <Box sx={{ mb: 2 }}>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 400,
                  color: 'custom.textBlue',
                  mb: 0.5,
                }}
              >
                Point of Contact Name
              </Typography>
              <TextField
                fullWidth
                placeholder="Enter POC name"
                value={brandFormData.poc_at_brand}
                onChange={e =>
                  handleBrandInputChange('poc_at_brand', e.target.value)
                }
                disabled={sameAsBusinessContact}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: '40px',
                    borderRadius: '6px',
                    bgcolor: sameAsBusinessContact ? '#f5f5f5' : 'white',
                    '& fieldset': {
                      borderWidth: '1px',
                      borderColor: 'lightGray.main',
                    },
                    '&:hover fieldset': {
                      borderColor: '#D0D5DD',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                      borderWidth: '1px',
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    padding: '8px 12px',
                    fontSize: '14px',
                  },
                }}
              />
            </Box>

            {/* POC Email */}
            <Box sx={{ mb: 2 }}>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 400,
                  color: 'custom.textBlue',
                  mb: 0.5,
                }}
              >
                Email
              </Typography>
              <TextField
                fullWidth
                type="email"
                placeholder="email@example.com"
                value={brandFormData.poc_at_email}
                onChange={e =>
                  handleBrandInputChange('poc_at_email', e.target.value)
                }
                disabled={sameAsBusinessContact}
                error={
                  isFormSubmitted &&
                  brandFormData.poc_at_email !== '' &&
                  !validateEmail(brandFormData.poc_at_email)
                }
                helperText={
                  isFormSubmitted &&
                  brandFormData.poc_at_email !== '' &&
                  !validateEmail(brandFormData.poc_at_email)
                    ? 'Invalid email address'
                    : ''
                }
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: '40px',
                    borderRadius: '6px',
                    bgcolor: sameAsBusinessContact ? '#f5f5f5' : 'white',
                    '& fieldset': {
                      borderWidth: '1px',
                      borderColor: 'lightGray.main',
                    },
                    '&:hover fieldset': {
                      borderColor: '#D0D5DD',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                      borderWidth: '1px',
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    padding: '8px 12px',
                    fontSize: '14px',
                  },
                }}
              />
            </Box>

            {/* POC Role */}
            <Box sx={{ mb: 2 }}>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 400,
                  color: 'custom.textBlue',
                  mb: 0.5,
                }}
              >
                Role
              </Typography>
              <TextField
                fullWidth
                placeholder="e.g., Manager"
                value={brandFormData.poc_role}
                onChange={e =>
                  handleBrandInputChange('poc_role', e.target.value)
                }
                disabled={sameAsBusinessContact}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: '40px',
                    borderRadius: '6px',
                    bgcolor: sameAsBusinessContact ? '#f5f5f5' : 'white',
                    '& fieldset': {
                      borderWidth: '1px',
                      borderColor: 'lightGray.main',
                    },
                    '&:hover fieldset': {
                      borderColor: '#D0D5DD',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                      borderWidth: '1px',
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    padding: '8px 12px',
                    fontSize: '14px',
                  },
                }}
              />
            </Box>

            {/* POC Contact */}
            <Box sx={{ mb: 2 }}>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 400,
                  color: 'custom.textBlue',
                  mb: 0.5,
                }}
              >
                Contact Number
              </Typography>
              <Box
                sx={{
                  '& .special-label': {
                    display: 'none !important',
                  },
                  '& .form-control::placeholder': {
                    opacity: 0,
                  },
                }}
              >
                <PhoneInput
                  country={'us'}
                  value={brandFormData.poc_contact}
                  onChange={phone =>
                    handleBrandInputChange('poc_contact', phone)
                  }
                  disabled={sameAsBusinessContact}
                  enableSearch={false}
                  disableSearchIcon={true}
                  disableDropdown={false}
                  searchPlaceholder=""
                  placeholder=""
                  inputStyle={{
                    width: '100%',
                    height: '40px',
                    fontSize: '14px',
                    borderRadius: '6px',
                    borderColor: '#E2E8F0',
                    backgroundColor: sameAsBusinessContact
                      ? '#f5f5f5'
                      : 'white',
                  }}
                  buttonStyle={{
                    borderRadius: '6px 0 0 6px',
                    border: '1px solid #E2E8F0',
                    backgroundColor: sameAsBusinessContact
                      ? '#f5f5f5'
                      : 'white',
                  }}
                  dropdownStyle={{
                    display: 'none',
                  }}
                  inputProps={{
                    name: 'contact_number',
                    required: true,
                    placeholder: '',
                  }}
                />
              </Box>
              {isFormSubmitted &&
                brandFormData.poc_contact &&
                brandFormData.poc_contact.length > 0 &&
                brandFormData.poc_contact.length < 10 && (
                  <Typography
                    sx={{
                      fontSize: '12px',
                      color: 'error.main',
                      mt: 0.5,
                    }}
                  >
                    Contact number must be at least 10 digits
                  </Typography>
                )}
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              {brands.length > 0 && (
                <Button
                  variant="outlined"
                  onClick={handleCancelEdit}
                  disabled={isPending}
                  sx={{
                    textTransform: 'none',
                    borderRadius: '8px',
                    height: '34px',
                    fontSize: '14px',
                    fontWeight: 500,
                  }}
                >
                  Cancel
                </Button>
              )}
              <Button
                variant="contained"
                startIcon={
                  isPending ? (
                    <CircularProgress size={16} />
                  ) : (
                    <Plus size={16} />
                  )
                }
                onClick={handleBrandSave}
                disabled={isPending}
                sx={{
                  textTransform: 'none',
                  borderRadius: '8px',
                  height: '34px',
                  fontSize: '14px',
                  fontWeight: 500,
                }}
              >
                {isPending
                  ? editingBrandId
                    ? 'Updating...'
                    : 'Adding...'
                  : editingBrandId
                    ? 'Update Brand'
                    : 'Add Brand'}
              </Button>
            </Box>
          </Box>
        )}

        {/* Added Brands List - Show when brands exist and form is hidden */}
        {brands.length > 0 && !showBrandForm && (
          <Box>
            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: 500,
                color: 'custom.textBlue',
                mb: 2,
              }}
            >
              Added Brands ({brands.length})
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {brands.map((brand: ICreateBrand) => (
                <Chip
                  key={brand.id}
                  label={brand.name}
                  icon={
                    <PencilSimple
                      size={16}
                      style={{ cursor: 'pointer', marginLeft: '8px' }}
                    />
                  }
                  onDelete={() => {
                    // Note: Delete functionality would need a delete API call
                    // For now, we'll just allow editing
                  }}
                  onClick={() => handleEditBrand(brand)}
                  deleteIcon={<X size={16} />}
                  sx={{
                    height: '32px',
                    bgcolor: 'custom.sidebarActiveBg',
                    color: 'custom.textBlue',
                    fontSize: '14px',
                    fontWeight: 500,
                    '& .MuiChip-icon': {
                      color: 'custom.textBlue',
                      marginLeft: '8px',
                      marginRight: '-6px',
                    },
                    '& .MuiChip-deleteIcon': {
                      color: 'lightGray.text',
                      '&:hover': {
                        color: 'error.main',
                      },
                    },
                  }}
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
});

BusinessAndBrandStep.displayName = 'BusinessAndBrandStep';

export default BusinessAndBrandStep;
